import {Component, Input, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';

import {EventService, PageRequest,} from '../../../../core';
import {AuthUser, TokenService} from '../../../../core/auth';
import {BreadcrumbService} from '../../../../breadcrumb.service';
import {Project, TelemetryResponse} from '../../../../core/models';
import {TelemetryService} from "../../../../core/services";
import {Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged} from 'rxjs/operators';
import {DataTable} from 'primeng/datatable';
import {ConfirmationService} from 'primeng/api';

const DAY = 24*60*60*1000;
@Component({
  templateUrl: './telemetry-data.page.html'
})
export class TelemetryDataPage implements OnInit {

  project: Project = null;
  telemetryResponse: TelemetryResponse = new TelemetryResponse();
  query: PageRequest = new PageRequest();
  @ViewChild('dt') dt: DataTable;
  searchTerms = new Subject<string>();
  loading = true;
  authUser: AuthUser = this.token.whoAmI();
  displayDialog: boolean = false;
  modalContent: String = '';
  selectedEvents: any[] = new Array();
  startDate: Date;
  endDate: Date;
  maxDate: Date;
  minDate: Date;
  modifiedEndDate: Date = new Date();
  cols: any[];
  _selectedColumns: any[];

  constructor(
    private telemetryService: TelemetryService,
    private route: ActivatedRoute,
    private eventService: EventService,
    private token: TokenService,
    private breadcrumbService: BreadcrumbService,
    private router: Router,
    private confirmationService: ConfirmationService
  ) {

  }

  ngOnInit() {
    this.initColumns();
    this.setDates();
    this.query.dateEnabled = false;
    this.searchTerms.pipe(
        debounceTime(1000),
        distinctUntilChanged())
        .subscribe(value => {
          this.dt.reset();
        });

    this.route.parent.data.subscribe((data) => {
      if (data.project) {
        this.project = Object.assign({}, data.project);

        this.breadcrumbService.setItems([
          {label: 'projects', routerLink: ['/project', 'list']},
          {label: this.project.name},
          {label: 'telemetry'},
        ]);
      }
    });
  }

  initColumns() {
    this.cols = [
      { field: 'creationDate', header: 'Received', sortField: 'creationDate', type: 'DATE', class: 'text-center overflow-ellipsis', default: true, width: '9%' },
      { field: 'eventId', header: 'Event Id', sortField: 'eventId', type: 'TEXT', class: 'text-center overflow-ellipsis', default: true, width: '9%' },
      { field: 'event', header: 'Event', sortField: '', type: 'EVENT', class: 'text-left overflow-ellipsis', default: true, width: '50%' },
      { field: 'transaction', header: 'Transaction', sortField: 'transaction', type: 'TRANSACTION', class: 'text-center overflow-ellipsis', default: true, width: '9%' },
      { field: 'transaction.versionName', header: 'Version Name', sortField: 'transaction.versionName', type: 'VERSION_NAME', class: 'text-center overflow-ellipsis', default: false, width: '9%' },
      { field: 'player', header: 'Player', sortField: 'player', type: 'PLAYER', class: 'text-center overflow-ellipsis', default: true, width: '9%' },
      { field: 'customerData', header: 'Customer Data', sortField: '', type: 'CUSTOMER_DATA', class: 'text-center overflow-ellipsis', default: true, width: '9%' }
    ];
    this._selectedColumns = this.cols.filter(col => col.default);
  }

  @Input() get selectedColumns(): any[] {
    return this._selectedColumns;
  }

  set selectedColumns(val: any[]) {
    this._selectedColumns = this.cols.filter(col => val.includes(col));
  }

  search(query: PageRequest): void {
    this.getTelemetry(query);
  }

  paginate(event: any) {
    this.query.page = (event.first / event.rows) + 1;
    this.query.sortField = event.sortField !== undefined ? event.sortField : 'name';
    this.query.sortDir = this.query.getSortDir(event.sortOrder);

    this.getTelemetry(this.query);
  }

  getTelemetry(query: any) {
    query.sortField = !query.sortField ? 'creationDate' : query.sortField;
    query.sortDir = !query.sortDir ? 'desc' : query.sortDir;
    query.startDate = this.dateToString(this.startDate);
    this.modifiedEndDate.setDate(this.endDate.getDate() + 1);
    query.endDate = this.dateToString(this.modifiedEndDate);

    this.loading = true;
    this.telemetryService.getTelemetryForProject(this.project.id, query)
      .subscribe(
        (data) => {
          this.telemetryResponse = data;
          this.selectedEvents = new Array();
          this.loading = false;
        },
        (error) => {
          this.selectedEvents = new Array();
          this.loading = false;
        }
      );
  }

  deleteEvents() {
    if (this.selectedEvents && this.selectedEvents.length > 0) {
      this.confirmationService.confirm({
        message: 'This action cannot be undone. Do you really want to delete the selected telemetry events?',
        accept: () => {
          this.loading = true;
          this.telemetryService.deleteTelemetryEvents(this.project.id, this.selectedEvents.map(x => x.id))
              .subscribe(
                  (data) => {
                    this.getTelemetry(this.query);
                  },
                  (error) => {
                    this.getTelemetry(this.query);
                  });
        }
      });
    }
  }

  showDialog(id: String) {
    this.displayDialog = true;
    for (let telemetry of this.telemetryResponse.content) {
      if (telemetry.customerData && telemetry.customerData.id === id) {
        this.modalContent = telemetry.customerData.data;
        return;
      }
    }
  }
  setMinDate() {
    if (typeof this.startDate !== 'undefined') {
      this.minDate.setTime(this.startDate.getTime() + (DAY));
    }
  }

  setMaxDate()  {
    if (typeof this.endDate !== 'undefined') {
      this.maxDate.setTime(this.endDate.getTime() - (DAY));
    }
  }

  private setDates() {
    this.startDate = new Date();
    this.startDate.setTime(this.startDate.getTime() - (DAY * 6));
    this.minDate = new Date();
    this.minDate.setTime(this.minDate.getTime() - (DAY * 5));
    this.endDate = new Date();
    this.maxDate = new Date();
    this.maxDate.setTime(this.maxDate.getTime() - (DAY));
  }

  dateToString(date: Date) : string {
    if (typeof date === 'undefined') {
      return null;
    } else {
      return  date.toLocaleDateString("fr-CA");
    }
  }
}
